(() => {
  const storageKey = 'scentiva-wishlist-handles';
  const labels = document.querySelector('#ScentivaWishlistConfig')?.dataset || {};
  const root = `${labels.scentivaWishlistRoot || '/'}`.replace(/\/?$/, '/');
  const drawer = document.querySelector('#ScentivaWishlist');
  const panel = drawer?.querySelector('.scentiva-wishlist-drawer__panel');
  let returnFocus;
  // Bounded by design: getSavedHandles caps at 100 handles and the Map is discarded on
  // navigation, so entries only accumulate within one page view. No eviction policy needed.
  const productCache = new Map();
  // Dawn's trapFocusHandlers (global.js) and body.overflow-hidden are both single shared
  // globals, so only tear down what this drawer actually established. focusTrapped covers
  // closing before renderDrawer() finished, when no trap was installed yet.
  //
  // Stacked focus traps stay broken by design. Dawn's trapFocus() calls removeTrapFocus()
  // unconditionally before binding, so any second trapping component kills the first one's
  // listeners with no way to restore them -- Dawn's own search modal over Dawn's own menu
  // drawer behaves identically. Fixing it requires forking global.js, which a Dawn upgrade
  // would conflict on.
  let focusTrapped = false;
  let bodyOverflowWasHidden = false;

  function text(key, fallback) {
    return labels[`scentivaWishlist${key}`] || fallback;
  }

  function getSavedHandles() {
    try {
      const value = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
      return Array.isArray(value)
        ? [...new Set(value.filter((handle) => typeof handle === 'string' && handle))].slice(0, 100)
        : [];
    } catch {
      return [];
    }
  }

  function saveHandles(handles) {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(handles));
    } catch {
      // Browser-local wishlist remains optional when storage is unavailable.
    }
  }

  // Polish needs three plural forms and the count is only known client-side, so keep the number
  // out of the noun's way: the label is a prefix and is never inflected.
  function countLabel(count) {
    const itemLabel = text('SavedItems', 'Saved items');
    return `${itemLabel}: ${count}`;
  }

  function updateButtons() {
    const handles = new Set(getSavedHandles());
    document.querySelectorAll('[data-scentiva-wishlist]').forEach((button) => {
      const saved = handles.has(button.dataset.scentivaWishlist);
      const title = button.getAttribute('data-scentiva-wishlist-title');
      const label = saved ? text('Remove', 'Remove from wishlist') : text('Save', 'Save to wishlist');
      button.setAttribute('aria-pressed', String(saved));
      button.setAttribute('aria-label', title ? `${label}: ${title}` : label);
      button.querySelector('[aria-hidden="true"]')?.replaceChildren(document.createTextNode(saved ? '♥' : '♡'));
    });

    document.querySelectorAll('[data-scentiva-wishlist-count]').forEach((element) => {
      element.textContent = countLabel(handles.size);
    });

    document.querySelectorAll('[data-scentiva-wishlist-bubble]').forEach((bubble) => {
      bubble.hidden = handles.size === 0;
      bubble.textContent = handles.size > 9 ? '9+' : String(handles.size);
    });

    document.querySelectorAll('[data-scentiva-wishlist-open]').forEach((button) => {
      const openLabel = text('OpenLabel', 'Open wishlist');
      button.setAttribute('aria-label', handles.size > 0 ? `${openLabel}, ${countLabel(handles.size)}` : openLabel);
    });
  }

  function productUrl(handle) {
    return `${root}products/${encodeURIComponent(handle)}.js`;
  }

  function formatPrice(cents) {
    const amount = Number(cents) / 100;
    if (!Number.isFinite(amount)) return '';
    // money_format is a shop-wide, locale-independent setting, so match the | money filter for
    // every interface language instead of following request.locale.
    const locale = 'pl';
    const currency = window.Shopify?.currency?.active;
    if (!currency) return new Intl.NumberFormat(locale).format(amount);
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
  }

  function productMarkup(product) {
    const item = document.createElement('article');
    item.className = 'scentiva-wishlist-item';

    const link = document.createElement('a');
    link.href = product.url || `${root}products/${encodeURIComponent(product.handle)}`;
    link.className = 'scentiva-wishlist-item__image';
    link.setAttribute('aria-label', `${text('ViewProduct', 'View product')}: ${product.title}`);
    if (product.featured_image) {
      const image = document.createElement('img');
      // featured_image from /products/<handle>.js is the unsized master; ask for the same
      // ?width= variant image_url emits. The base also resolves the protocol-relative //cdn form.
      const source = new URL(product.featured_image, window.location.href);
      source.searchParams.set('width', '320');
      image.src = source.href;
      image.alt = product.title;
      image.loading = 'lazy';
      image.width = 160;
      image.height = 200;
      link.append(image);
    }

    const details = document.createElement('div');
    details.className = 'scentiva-wishlist-item__details';
    const title = document.createElement('a');
    title.href = link.href;
    title.className = 'scentiva-wishlist-item__title';
    title.textContent = product.title;
    const price = document.createElement('p');
    price.className = 'scentiva-wishlist-item__price';
    price.textContent = formatPrice(product.price);
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'scentiva-wishlist-item__remove';
    remove.dataset.scentivaWishlist = product.handle;
    remove.dataset.scentivaWishlistTitle = product.title;
    remove.textContent = text('Remove', 'Remove from wishlist');
    details.append(title, price, remove);
    item.append(link, details);
    return item;
  }

  async function renderDrawer() {
    const content = drawer?.querySelector('[data-scentiva-wishlist-content]');
    if (!content) return;
    const handles = getSavedHandles();
    content.replaceChildren();
    if (!handles.length) {
      const empty = document.createElement('div');
      empty.className = 'scentiva-wishlist-empty';
      const icon = document.createElement('p');
      icon.className = 'scentiva-wishlist-empty__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = '♡';
      const title = document.createElement('h3');
      title.className = 'scentiva-wishlist-empty__title';
      title.textContent = text('EmptyTitle', 'Save what speaks to you');
      const message = document.createElement('p');
      message.className = 'scentiva-wishlist-empty__text';
      message.textContent = text(
        'EmptyText',
        'Use the heart on any product and it will wait here for your next ritual.',
      );
      empty.append(icon, title, message);
      content.append(empty);
      return;
    }

    const loading = document.createElement('p');
    loading.className = 'scentiva-wishlist-status';
    loading.textContent = text('Loading', 'Loading saved products…');
    content.append(loading);
    const results = await Promise.all(
      handles.map(async (handle) => {
        if (productCache.has(handle)) return { handle, product: productCache.get(handle) };
        try {
          const response = await fetch(productUrl(handle), { credentials: 'same-origin' });
          if (response.status === 404) return { handle, missing: true };
          if (!response.ok) return { handle, unavailable: true };
          const product = await response.json();
          productCache.set(handle, product);
          return { handle, product };
        } catch {
          return { handle, unavailable: true };
        }
      }),
    );
    const products = results.flatMap(({ product }) => (product ? [product] : []));
    const missingHandles = new Set(results.filter(({ missing }) => missing).map(({ handle }) => handle));
    if (missingHandles.size) {
      saveHandles(handles.filter((handle) => !missingHandles.has(handle)));
      missingHandles.forEach((handle) => productCache.delete(handle));
    }
    updateButtons();
    if (!products.length) {
      // Handles pruned as confirmed 404s leave an empty wishlist, not a failed one. Storage is
      // empty by then, so the re-entry takes the empty-state branch above and never fetches again.
      if (getSavedHandles().length) renderUnavailable();
      else await renderDrawer();
      return;
    }
    content.replaceChildren();
    products.forEach((product) => content.append(productMarkup(product)));
    // productMarkup sets data-scentiva-wishlist-title but no aria-label; the call above ran
    // while the panel still held the loading paragraph, so name the remove buttons now.
    updateButtons();
  }

  function renderStatus(message) {
    const content = drawer?.querySelector('[data-scentiva-wishlist-content]');
    if (!content) return;
    const status = document.createElement('p');
    status.className = 'scentiva-wishlist-status';
    status.textContent = message;
    content.replaceChildren(status);
  }

  function renderUnavailable() {
    renderStatus(text('Unavailable', 'Saved products are unavailable right now. Please try again.'));
  }

  async function openDrawer(trigger) {
    if (!drawer || !panel) return;
    // A repeat activation while the products are still loading would recompute
    // bodyOverflowWasHidden from the class this call already added and strand overflow-hidden.
    if (!drawer.hidden) return;
    returnFocus = trigger || document.activeElement;
    drawer.hidden = false;
    bodyOverflowWasHidden = document.body.classList.contains('overflow-hidden');
    if (!bodyOverflowWasHidden) document.body.classList.add('overflow-hidden');
    // The panel is aria-modal and covers the trigger immediately, so move focus inside before
    // awaiting the fetches; trapFocus below still arms the boundaries once the items exist.
    panel.focus();
    document
      .querySelectorAll('[data-scentiva-wishlist-open]')
      .forEach((button) => button.setAttribute('aria-expanded', 'true'));
    await renderDrawer();
    if (drawer.hidden) return;
    trapFocus(panel, panel.querySelector('[data-scentiva-wishlist-close]') || panel);
    focusTrapped = true;
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.hidden = true;
    if (!bodyOverflowWasHidden) document.body.classList.remove('overflow-hidden');
    document
      .querySelectorAll('[data-scentiva-wishlist-open]')
      .forEach((button) => button.setAttribute('aria-expanded', 'false'));
    const opener = returnFocus instanceof HTMLElement ? returnFocus : null;
    if (focusTrapped) {
      focusTrapped = false;
      removeTrapFocus(opener);
      return;
    }
    opener?.focus();
  }

  document.addEventListener('click', (event) => {
    const open = event.target.closest('[data-scentiva-wishlist-open]');
    if (open) {
      event.preventDefault();
      openDrawer(open).catch(renderUnavailable);
      return;
    }
    if (event.target.closest('[data-scentiva-wishlist-close]')) {
      closeDrawer();
      return;
    }
    const add = event.target.closest('[data-scentiva-quick-add]');
    if (add) {
      event.preventDefault();
      quickAdd(add);
      return;
    }
    const button = event.target.closest('[data-scentiva-wishlist]');
    if (!button) return;
    event.preventDefault();
    const handle = button.dataset.scentivaWishlist;
    if (!handle) return;

    const handles = new Set(getSavedHandles());
    if (handles.has(handle)) {
      handles.delete(handle);
    } else {
      handles.add(handle);
    }
    saveHandles([...handles]);
    updateButtons();
    if (drawer && !drawer.hidden) {
      // renderDrawer() replaces every child, including the remove button that was just
      // activated and the node trapFocus captured as its last element, so re-arm the trap.
      renderDrawer()
        .then(() => {
          if (focusTrapped && !drawer.hidden) {
            trapFocus(panel, panel.querySelector('[data-scentiva-wishlist-close]') || panel);
          }
        })
        .catch(renderUnavailable);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && drawer && !drawer.hidden) closeDrawer();
  });

  // Quick add from the card action bar: posts the default variant to Dawn's cart AJAX, then
  // refreshes the same sections product-form.js does (cart drawer, header bubble) so every
  // Dawn surface stays in sync. The bar reserves its layout space, so no reflow on success.
  async function quickAdd(button) {
    const variantId = button.dataset.scentivaQuickAdd;
    if (!variantId || button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    try {
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const body = new URLSearchParams({ id: variantId, quantity: '1' });
      if (cart) {
        body.set('sections', cart.getSectionsToRender().map((section) => section.id).join(','));
        body.set('sections_url', window.location.pathname);
      }
      const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body,
        credentials: 'same-origin',
      });
      const parsed = await response.json();
      if (!response.ok || parsed.status) {
        throw new Error(parsed.description || parsed.message || 'Unable to add to cart');
      }
      if (cart) {
        cart.renderContents(parsed);
      } else {
        window.location = window.routes?.cart_url || '/cart';
      }
    } catch (error) {
      console.error(error);
      alert(window.cartStrings?.error || error.message);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  // Every heart is server-rendered unsaved, so cards that arrive after load show a stale glyph
  // whose next click does the opposite of what it promises. Dawn swaps these two containers
  // wholesale (facets.js filter/sort, product-recommendations), and another tab reports through
  // storage. childList only: updateButtons' own glyph swap is deeper and cannot self-trigger.
  window.addEventListener('storage', updateButtons);
  document
    .querySelectorAll('#ProductGridContainer, product-recommendations')
    .forEach((node) => new MutationObserver(updateButtons).observe(node, { childList: true }));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateButtons, { once: true });
  } else {
    updateButtons();
  }
})();
